import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useI18n } from '../contexts/I18nContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// In-memory client cache — never re-hit backend for a session's already-fetched (doc, lang)
const memCache = new Map();

/**
 * useTranslatedItems — translates an array of English strings into the active
 * language on-demand. First call per (doc, lang) hits the backend which caches
 * in MongoDB; subsequent calls in the same tab are instant from memory.
 *
 * Returns [translatedItems, loading].
 * If active language is English (or translation fails), returns the input as-is.
 */
export default function useTranslatedItems(docId, items) {
  const { language } = useI18n();
  const [translated, setTranslated] = useState(items);
  const [loading, setLoading] = useState(false);

  const signature = useMemo(() => {
    // Stable cache key: doc + lang + item count + first & last item snippet
    const first = items?.[0]?.slice(0, 40) || '';
    const last = items?.[items.length - 1]?.slice(0, 40) || '';
    return `${docId}|${language}|${items?.length}|${first}|${last}`;
  }, [docId, language, items]);

  useEffect(() => {
    if (!items || items.length === 0) {
      setTranslated(items);
      return;
    }
    if (language === 'en') {
      setTranslated(items);
      return;
    }
    // Check memory cache first
    if (memCache.has(signature)) {
      setTranslated(memCache.get(signature));
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await axios.post(`${API}/i18n/translate`, {
          doc_id: docId,
          lang: language,
          items,
        });
        if (cancelled) return;
        if (res.data?.items?.length === items.length) {
          memCache.set(signature, res.data.items);
          setTranslated(res.data.items);
        } else {
          setTranslated(items);
        }
      } catch {
        if (!cancelled) setTranslated(items);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [signature, docId, language, items]);

  return [translated, loading];
}
